import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment, { Moment } from "moment-timezone";
import { twMerge } from "tailwind-merge";
import { AppointmentSummary } from "../../../../hooks/agenda";
import NoTranslate from "../../../atoms/NoTranslate";
import { appointmentsOnDay } from "./agendaDates";
import { PENDING_HATCH, paymentViewOf } from "./agendaPayment";

const MAX_VISIBLE = 3;

interface MonthGridProps {
  days: Moment[];
  month: Moment;
  appointments: AppointmentSummary[];
  canCreate: boolean;
  onCreate: (start: number) => void;
  onOpen: (appointment: AppointmentSummary) => void;
  onSelectDay: (day: Moment) => void;
}

const MonthGrid: React.FC<MonthGridProps> = ({
  days,
  month,
  appointments,
  canCreate,
  onCreate,
  onOpen,
  onSelectDay,
}) => {
  const { t } = useTranslation();
  const gridRef = useRef<HTMLDivElement>(null);
  const today = moment();
  const now = today.valueOf();
  const defaultFocus = useMemo(() => {
    const todayIndex = days.findIndex((d) => d.isSame(moment(), "day"));
    if (todayIndex >= 0) return todayIndex;
    return Math.max(
      0,
      days.findIndex((d) => d.isSame(month, "month"))
    );
  }, [days, month]);
  const [focusIndex, setFocusIndex] = useState(defaultFocus);

  useEffect(() => {
    setFocusIndex(defaultFocus);
  }, [defaultFocus]);

  const focusDay = (index: number) => {
    const next = Math.max(0, Math.min(days.length - 1, index));
    setFocusIndex(next);
    gridRef.current
      ?.querySelector<HTMLButtonElement>(`[data-day="${next}"]`)
      ?.focus();
  };

  const onDayKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    const moves: Record<string, number> = {
      ArrowLeft: index - 1,
      ArrowRight: index + 1,
      ArrowUp: index - 7,
      ArrowDown: index + 7,
      Home: index - (index % 7),
      End: index - (index % 7) + 6,
    };
    if (!(event.key in moves)) return;
    event.preventDefault();
    focusDay(moves[event.key]);
  };

  return (
    <div className="min-w-0">
      <div className="grid grid-cols-7 border-b border-gray-200">
        {days.slice(0, 7).map((day) => (
          <div
            key={day.valueOf()}
            className="truncate px-1 py-2 text-center text-xs text-gray-500"
          >
            {moment.localeData().weekdaysShort(day)}
          </div>
        ))}
      </div>
      <div
        ref={gridRef}
        role="group"
        aria-label={t("agenda.grid.monthLabel") as string}
        className="grid grid-cols-7"
      >
        {days.map((day, index) => {
          const dayAppointments = appointmentsOnDay(appointments, day);
          const visible = dayAppointments.slice(0, MAX_VISIBLE);
          const hidden = dayAppointments.length - visible.length;
          const inMonth = day.isSame(month, "month");
          const isToday = day.isSame(today, "day");
          return (
            <div
              key={day.valueOf()}
              onClick={
                canCreate
                  ? (event) => {
                      if (event.target !== event.currentTarget) return;
                      onCreate(day.clone().hour(9).startOf("hour").valueOf());
                    }
                  : undefined
              }
              className={twMerge(
                "flex min-h-24 min-w-0 flex-col gap-0.5 border-b border-gray-100 p-1",
                index % 7 !== 0 && "border-l",
                inMonth ? "bg-white" : "bg-gray-50",
                canCreate && "cursor-pointer"
              )}
            >
              <button
                type="button"
                data-day={index}
                tabIndex={focusIndex === index ? 0 : -1}
                onFocus={() => setFocusIndex(index)}
                onKeyDown={(e) => onDayKeyDown(e, index)}
                onClick={() => onSelectDay(day)}
                aria-label={
                  t("agenda.grid.openDay", {
                    date: day.format("LL"),
                    count: dayAppointments.length,
                  }) as string
                }
                className={twMerge(
                  "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
                  inMonth ? "text-gray-900" : "text-gray-400",
                  isToday && "bg-blue-600 text-white hover:bg-blue-700"
                )}
              >
                {day.date()}
              </button>

              {dayAppointments.length > 0 && (
                <span
                  aria-hidden="true"
                  className="flex w-7 justify-center gap-0.5 pt-0.5 sm:hidden"
                >
                  {visible.map((appointment) => (
                    <span
                      key={appointment.id}
                      className={twMerge(
                        "h-1.5 w-1.5 rounded-full bg-blue-600",
                        appointment.end <= now && "bg-gray-400"
                      )}
                    />
                  ))}
                </span>
              )}

              <div className="hidden min-w-0 flex-col gap-0.5 sm:flex">
                {visible.map((appointment) => {
                  const isPast = appointment.end <= now;
                  const payment = paymentViewOf(appointment);
                  const startsToday = day.isSame(appointment.start, "day");
                  const endsToday = day.isSame(appointment.end - 1, "day");
                  return (
                    <button
                      key={appointment.id}
                      type="button"
                      onClick={() => onOpen(appointment)}
                      aria-label={[
                        t("agenda.grid.openAppointment", {
                          title: appointment.title,
                          date: moment(appointment.start).format("LLLL"),
                        }),
                        payment && t(`agenda.payment.${payment}`),
                      ]
                        .filter(Boolean)
                        .join(", ")}
                      className={twMerge(
                        "relative flex min-w-0 items-center gap-1 overflow-hidden rounded-sm border-l-2 border-blue-600 bg-blue-50 px-1 text-left text-xs leading-5 text-blue-900 transition-colors hover:bg-blue-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
                        isPast &&
                          "border-gray-400 bg-gray-50 text-gray-700 hover:bg-gray-100",
                        payment === "pending" &&
                          "border-amber-500 bg-amber-50 text-amber-900 hover:bg-amber-100"
                      )}
                    >
                      {payment === "pending" && (
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute inset-0 text-amber-200"
                          style={PENDING_HATCH}
                        />
                      )}
                      {payment === "due" && (
                        <span
                          aria-hidden="true"
                          className="relative h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                        />
                      )}
                      <span
                        className={twMerge(
                          "relative shrink-0 tabular-nums text-blue-700",
                          isPast && "text-gray-500",
                          payment === "pending" && "text-amber-800"
                        )}
                      >
                        {startsToday
                          ? moment(appointment.start).format("LT")
                          : endsToday
                          ? `→ ${moment(appointment.end).format("LT")}`
                          : "→"}
                      </span>
                      <span className="relative truncate font-medium">
                        <NoTranslate>{appointment.title}</NoTranslate>
                      </span>
                    </button>
                  );
                })}
                {hidden > 0 && (
                  <button
                    type="button"
                    onClick={() => onSelectDay(day)}
                    className="rounded-sm px-1 text-left text-xs text-gray-500 hover:text-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  >
                    {t("agenda.grid.more", { count: hidden })}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MonthGrid;
